import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { COMPLIANT_LABEL, NON_COMPLIANT_LABEL, FIXED_COMPLIANT_LABEL, PARTIALLY_COMPLIANT_LABEL } from "src/constants/constants";
import { Button, Column, Icon, Row, Sheet, Text, Loader } from "@amzn/meridian";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import { ObjectPresenceCheck, Evidence, isOverridden, isFixedNow, isFixedLater, resolveDisplayedComment } from "src/services/inspectionService";
import { formatAttestationDateTime } from "src/utils/dateUtil";
import "./ComplianceDetailSheet.scss";
import ThemedTag from "./ThemedTag";
import OverrideIcon from "./OverrideIcon";
import EvidenceTabs from "./EvidenceTabs";

interface ObjectPresenceDetailSheetProps {
    open: boolean;
    onClose: () => void;
    check: ObjectPresenceCheck | null;
    onOverride?: (check: ObjectPresenceCheck) => void;
    /** Fix-it-now: only offered for a non-compliant object check. */
    onFixNow?: (check: ObjectPresenceCheck) => void;
    /** Fix-it-later: only offered for a non-compliant object check not already deferred. */
    onFixLater?: (check: ObjectPresenceCheck) => void;
    isAnalysisPending?: boolean;
    evidence: Evidence[];
    presignedUrlMap: Record<string, string>;
    /** Human-readable question text from the workflow question config. */
    questionText?: string;
    /** Whether the parent checkpoint is verified. A verified checkpoint locks its
     *  answer, so override is hidden. Verification is question-level. */
    verified?: boolean;
    /** Verification audit fields (question-level), shown as an attestation line. */
    verifiedBy?: string | null;
    verifiedAt?: string | null;
}

const ObjectPresenceDetailSheet: React.FC<ObjectPresenceDetailSheetProps> = ({
    open,
    onClose,
    check,
    onOverride,
    onFixNow,
    onFixLater,
    isAnalysisPending = false,
    evidence,
    presignedUrlMap,
    questionText,
    verified = false,
    verifiedBy,
    verifiedAt,
}) => {
    const [expandedUrl, setExpandedUrl] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<"overridden" | "original">("overridden");

    useEffect(() => {
        setExpandedUrl(null);
        setActiveTab("overridden");
    }, [check, open]);

    if (!check) {
        return null;
    }

    const fixedNow = isFixedNow(check);
    const fixedLater = isFixedLater(check);
    const fixEvidences = check.complianceFixDetails?.evidences ?? [];

    // Fall back to fix photos when tabs are suppressed, so fix evidence is never dropped.
    const evidenceUrls = (fixedNow ? fixEvidences : evidence)
        .map((e) => presignedUrlMap[e.uri])
        .filter(Boolean);

    const shouldShowTabs = fixedNow
        ? fixEvidences.length > 0 && evidence.length > 0
        : isOverridden(check) && evidence.length > 0;

    const isOriginalTabActive = shouldShowTabs && activeTab === "original";
    // On the original tab of an overridden check, show the pre-override verdict.
    // A fixed-now check was always originally non-compliant.
    const displayCompliant = fixedNow
        ? !isOriginalTabActive
        : (isOriginalTabActive && check.originalIsCompliant !== undefined ? check.originalIsCompliant : check.is_compliant);

    // Object presence checks have no answer-time comment; a fixed-now/fixed-later check
    // shows the fix comment (the deferral reason for fix-later).
    const commentText = fixedLater
        ? check.complianceFixDetails?.comment?.trim()
        : fixedNow
            ? (isOriginalTabActive ? undefined : check.complianceFixDetails?.comment?.trim())
            : resolveDisplayedComment(
                isOverridden(check), isOriginalTabActive, check.overrideReason, undefined
            );

    return (
        <>
        <Sheet type="overlay" side="bottom" open={open} onClose={onClose} className="compliance-detail-sheet">
            <Column spacing="400" spacingInset="500">
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Text type="h300">Compliance Details</Text>
                    <Button onClick={onClose} type="icon">
                        <Icon tokens={closeSmallTokens}>Close compliance details</Icon>
                    </Button>
                </Row>

                {questionText && (
                    <Text type="b200" color="secondary">
                        {questionText}
                    </Text>
                )}

                <Row alignmentHorizontal="justify" alignmentVertical="top">
                    <Column spacing="100">
                        <Text type="b100" color="secondary">Object</Text>
                        <Text type="b200" color="primary">{check.object_name}</Text>
                    </Column>
                    <Row spacing="200" alignmentVertical="center">
                        <ThemedTag
                            text={isAnalysisPending ? "Analyzing..."
                                : fixedNow && !isOriginalTabActive ? FIXED_COMPLIANT_LABEL
                                    : fixedLater && !isOriginalTabActive ? PARTIALLY_COMPLIANT_LABEL
                                        : displayCompliant ? COMPLIANT_LABEL : NON_COMPLIANT_LABEL}
                            type={isAnalysisPending ? "neutral"
                                : fixedNow && !isOriginalTabActive ? "fixCompliant"
                                    : fixedLater && !isOriginalTabActive ? "fixLater"
                                        : displayCompliant ? "success" : "error"}
                        />
                        {/* Object presence questions are always AI-analyzed, matching the
                            hardcoded source tag on ObjectPresenceCheckpointCard. */}
                        {!isAnalysisPending && <ThemedTag text="AI" type="neutral" />}
                    </Row>
                </Row>

                {isAnalysisPending ? (
                    <Loader type="circular" size="large" />
                ) : (
                    <>
                        <Column spacing="100">
                            <Text type="b100" color="secondary">Answer</Text>
                            <Row spacing="200" alignmentVertical="center">
                                <Column spacing="200">
                                <Text type="b200" color="primary">{displayCompliant ? "YES" : "NO"}</Text>
                                {!fixedNow && isOverridden(check) && activeTab !== "original" && (
                                    <ThemedTag text="Manually Overridden" type="override" icon={<OverrideIcon />} />
                                )}
                                </Column>
                                
                            </Row>
                        </Column>

                        {(!shouldShowTabs || activeTab === "original") && check.ai_reason && (
                            <Column spacing="100">
                                <Text type="b100" color="secondary">Reason</Text>
                                <Text type="b200" color="primary">{check.ai_reason}</Text>
                            </Column>
                        )}

                        {fixedLater && commentText && (
                            <Column spacing="100">
                                <Text type="b100" color="secondary">Reason Deferred</Text>
                                <Text type="b200" color="primary">{commentText}</Text>
                            </Column>
                        )}

                        {shouldShowTabs ? (
                            <EvidenceTabs
                                activeTab={activeTab}
                                onTabChange={setActiveTab}
                                overriddenEvidences={fixedNow ? fixEvidences : (check.overriddenEvidences ?? [])}
                                originalEvidences={evidence}
                                presignedUrlMap={presignedUrlMap}
                                onImageExpand={setExpandedUrl}
                                firstTabLabel={fixedNow ? "Fix Evidence" : undefined}
                            />
                        ) : evidenceUrls.length > 0 && (
                            <Row spacing="300" wrap="down">
                                {evidenceUrls.map((url, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        className="compliance-detail-sheet__image-button"
                                        onClick={() => setExpandedUrl(url)}
                                        aria-label={`Expand evidence ${idx + 1}`}
                                    >
                                        <img
                                            src={url}
                                            alt={`Evidence ${idx + 1}`}
                                            className={`compliance-detail-sheet__image${evidenceUrls.length === 1 ? " compliance-detail-sheet__image--large" : ""}`}
                                        />
                                    </button>
                                ))}
                            </Row>
                        )}

                        {!fixedLater && commentText && (
                            <Column spacing="100">
                                <Text type="h200" color="primary">Comments</Text>
                                <Text type="b200" color="secondary">{commentText}</Text>
                            </Column>
                        )}

                        {verified && verifiedAt && (
                            <Text type="b100" color="secondary">
                                Attested by {verifiedBy} at {formatAttestationDateTime(verifiedAt)}
                            </Text>
                        )}

                        {!isAnalysisPending && !fixedNow && !fixedLater && !verified && !isOriginalTabActive && (onFixNow || onOverride || onFixLater) && (
                            <Column spacing="300">
                                {onFixNow && !check.is_compliant && (
                                    <button
                                        className="compliance-detail-sheet__fix-now-btn"
                                        onClick={() => onFixNow(check)}
                                    >
                                        FIX IT NOW
                                    </button>
                                )}

                                <Row spacing="300" widths="fill">
                                    {onOverride && (
                                        <button
                                            className="compliance-detail-sheet__override-btn"
                                            onClick={() => onOverride(check)}
                                        >
                                            OVERRIDE
                                        </button>
                                    )}
                                    {onFixLater && !check.is_compliant && (
                                        <button
                                            className="compliance-detail-sheet__fix-later-btn"
                                            onClick={() => onFixLater(check)}
                                        >
                                            FIX LATER
                                        </button>
                                    )}
                                </Row>
                            </Column>
                        )}
                    </>
                )}
            </Column>
        </Sheet>

        {expandedUrl && ReactDOM.createPortal(
            <div
                className="compliance-detail-sheet__lightbox"
                aria-label="Expanded evidence image"
                onClick={() => setExpandedUrl(null)}
            >
                <button
                    type="button"
                    className="compliance-detail-sheet__lightbox-close"
                    onClick={() => setExpandedUrl(null)}
                    aria-label="Close expanded image"
                >
                    &times;
                </button>
                <img
                    src={expandedUrl}
                    alt="Expanded evidence"
                    className="compliance-detail-sheet__lightbox-image"
                    onClick={(e) => e.stopPropagation()}
                />
            </div>,
            document.body
        )}
        </>
    );
};

export default ObjectPresenceDetailSheet;
